import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { deriveCompanyRoleFlags } from '@/lib/company-roles';

interface CompanyInfo {
  companyId: string;
  companyName: string;
  role: 'manager' | 'coordinator' | 'employee';
  logoUrl: string | null;
}

export function useCompany() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['company-membership', user?.id],
    queryFn: async (): Promise<CompanyInfo | null> => {
      if (!user?.id) return null;

      const { data: membership, error } = await supabase
        .from('company_members')
        .select('company_id, role, care_companies(id, name, logo_url)')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (error || !membership) return null;

      const company = membership.care_companies as any;
      return {
        companyId: membership.company_id,
        companyName: company?.name ?? '',
        role: membership.role as CompanyInfo['role'],
        logoUrl: company?.logo_url ?? null,
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const roleFlags = deriveCompanyRoleFlags(data?.role ?? null);

  return {
    company: data ?? null,
    isLoading,
    ...roleFlags,
  };
}
